/**
 * 하네스 워크플로 — 개발요청이 들어와서 배포·문서화까지 가는 길과, 그 길에서 부르는 스킬 순서.
 *
 * 스킬 이름과 산출물은 `ai-harness-common/skills/<스킬>/SKILL.md`가 정본이다. 스킬이 바뀌면 여기도 고친다 —
 * 이 화면은 "다음에 뭘 부르지?"에 답하는 지도이지 스킬 설명서가 아니다.
 */
export interface FlowStep {
  title: string;
  /** 이 단계에서 부르는 스킬. 사람이 직접 하는 단계(슬랙 등록·수동 검증)는 비운다. */
  skill?: string;
  detail: string;
  /** 이 단계가 남기는 것. 다음 단계가 읽는 입력이기도 하다. */
  output?: string;
  /** 누가 하는 단계인가 — 다이어그램에서 색으로 가른다. 스킬 순서 표에서는 쓰지 않는다. */
  actor?: FlowActor;
}

/** requester: 요청자(기획·운영·CS 포함) · auto: 콘솔·봇이 알아서 · dev: 담당 개발자 · qa: QA */
export type FlowActor = 'requester' | 'auto' | 'dev' | 'qa';

export interface Flow {
  key: string;
  title: string;
  /** 언제 이 흐름을 타는가. */
  when: string;
  steps: FlowStep[];
}

export interface HarnessTool {
  skill: string;
  when: string;
}

/**
 * 개발요청부터 배포까지 — 슬랙에 요청서를 올린 것이 QA를 거쳐 나가기까지.
 * 두 번째 단계(콘솔 적재·알림)는 사람이 하는 일이 없다. 접수와 동시에 알림·하네스 기록·노션 카드가 함께 돌고,
 * 그중 하나가 실패해도 접수는 유지된다.
 */
export const DEV_REQUEST_FLOW: FlowStep[] = [
  {
    title: '📝 개발요청서 등록',
    actor: 'requester',
    skill: 'nd-prd-draft',
    detail:
      '요청서를 슬랙 스레드에 올리고 봇을 멘션해 주세요(`@봇 개발요청 등록해줘`). 요청서는 기획·운영·CS도 `nd-prd-draft`로 자연어와 스크린샷만으로 쓸 수 있습니다. 고친 요청서를 같은 스레드에 다시 멘션하면 새 요청이 아니라 그 요청의 다음 버전이 됩니다',
    output: '사전 PRD(기능 요청서)',
  },
  {
    title: '🤖 콘솔에 쌓이고 슬랙 알림 (자동)',
    actor: 'auto',
    detail:
      '봇이 제목·영문 slug을 지어 콘솔로 넘기면, 콘솔이 요청을 저장하면서 채널 알림을 보내고 하네스 저장소에 원문을 커밋하고(기본 브랜치에 바로, PR 없음) 노션 카드를 세웁니다. 프로젝트 보드 → 개발요청에서 확인할 수 있습니다',
    output: '`market-ai-harness/dev-requests/YYYY/MM/YYYY-MM-DD-<slug>/<slug>.md` · 노션 카드',
  },
  {
    title: '🙋 담당자 지정',
    actor: 'dev',
    detail:
      '프로젝트 보드 → 개발요청에서 담당자를 골라 주세요. 하네스 원문과 노션 카드에 담당자가 다시 적히고, 원 요청 스레드에 "담당자가 지정됐습니다"가 달립니다',
  },
  {
    title: '🛠️ 하네스로 개발',
    actor: 'dev',
    skill: 'nd-prd-intake → nd-feature-loop',
    detail:
      '담당자가 요청 원문을 PRD로 옮기고(`nd-prd-intake`) 아래 피처 흐름을 탑니다 — 설계 → 스펙 리뷰 → task 분해 → 구현·리뷰·e2e·stage(`nd-feature-loop`). 스펙 폴더가 생기면 보드에 에픽으로 뜹니다',
    output: '`specs/YYYY/MM/<slug>/` · PR',
  },
  {
    title: '📄 역기획서 작성',
    actor: 'dev',
    skill: 'nd-qa-brief',
    detail:
      'e2e가 통과하면 바뀐 화면·동작·정책을 QA가 읽을 수 있는 말로 옮깁니다. 하네스에 올라오면 색인이 슬랙에 QA 인계 요청을 알립니다',
    output: '`nd-qa-brief.md`',
  },
  {
    title: '🔍 QA 요청',
    actor: 'qa',
    detail:
      '프로젝트 보드 → QA에서 역기획서에 검증 기간과 배포 목표일을 적어 주세요. QA가 하네스 `qa/`에 체크리스트와 결과를 남기면 색인돼 티켓(No Run·Fail·Block·Pass)으로 뜨고, 같은 화면의 달력·레인에서 진행 상황을 볼 수 있습니다',
  },
  {
    title: '🚀 QA 통과 → 배포',
    actor: 'dev',
    detail:
      '열린 티켓(No Run·Fail·Block)이 0건이 되면 콘솔이 "QA 완료"를 알립니다(티켓이 하나도 없으면 완료로 치지 않습니다). 그때 PR을 머지해 주세요 — 머지가 곧 운영 배포입니다. 이슈가 나오면 "하네스로 개발"로 돌아가 고칩니다',
  },
];

export const FLOWS: Flow[] = [
  {
    key: 'feature',
    title: '✨ 피처',
    when: '새 기능 — 개발요청이 PRD가 된 뒤에 탑니다',
    steps: [
      { title: '설계', skill: 'nd-prd-to-feature-spec', detail: 'PRD를 읽고 설계 정본을 씁니다. 위험 신호에 따라 lld 범위를 고릅니다', output: '`HLD.md` · `lld/*.md`' },
      { title: '스펙 리뷰', skill: 'nd-spec-review', detail: '외부 AI 리뷰어가 실제 코드까지 대조해 검토하고 피드백을 반영합니다. task 분해 전 필수 게이트입니다', output: '`spec-review.md`' },
      { title: 'task 분해', skill: 'nd-prd-to-parallel-tasks', detail: '병렬로 돌릴 수 있는 task로 쪼갭니다. 스펙이 바뀌면 새로 만들지 않고 기존 task를 고칩니다', output: '`TASKS.md` · `tasks/**` · `EXECUTION_RULES.md` · `PROGRESS.md`' },
      {
        title: '구현 → 검증 → 완료',
        skill: 'nd-feature-loop',
        detail:
          '구현(worker) → 리뷰 → e2e 체크리스트 → worktree e2e 실행 → stage → 머지 확인 → 검증 주체(dev|qa) 판정 → QA면 역기획서(`nd-qa-brief`) → 수동 검증까지 기계 게이트로 조율합니다. 위험한 전이는 하나씩 승인을 받습니다',
        output: '`REVIEW.md` · `e2e-result.md` · PR',
      },
      { title: '스펙 동기화 감사', skill: 'nd-spec-sync-audit', detail: '머지된 코드가 PRD·HLD·lld·tasks에 반영됐는지 봅니다. 갭이 있으면 멈추고 설계부터 다시 돕니다', output: '`spec-sync.md`' },
      { title: '지식베이스 반영', skill: 'nd-docs-maintenance', detail: '끝난 작업을 INDEX·known-patterns·modules에 반영합니다', output: '`INDEX` · `known-patterns`' },
    ],
  },
  {
    key: 'issue',
    title: '🐛 버그 · 이슈',
    when: '운영 에러·버그 제보(Sentry·슬랙·스크린샷)가 들어왔을 때 탑니다',
    steps: [
      { title: '준비', skill: 'nd-issue-prepare', detail: '제보 정리 → 원인 조사(코드 수정 없음) → 수정 계획을 단계 게이트와 함께 한 번에 돕니다. 따로 부르려면 `nd-issue-intake` → `nd-issue-investigation` → `nd-issue-to-fix-plan` 순서로 불러 주세요', output: '`issue.md` · `investigation.md` · `fix-plan.md`' },
      { title: '수정 → 검증 → 완료', skill: 'nd-feature-loop', detail: 'fix-plan이 확정되면 피처와 같은 루프를 탑니다. 루프 없이 가려면 `nd-bugfix-execution` → `nd-bugfix-review` 순서로 불러 주세요', output: '`fix-result.md` · `review.md` · PR' },
      { title: '지식베이스 반영', skill: 'nd-docs-maintenance', detail: '재발 패턴을 known-patterns에 남깁니다' },
    ],
  },
  {
    key: 'incident',
    title: '🚨 인시던트',
    when: '운영 장애가 해소된 직후 — 재발 방지까지 이어 갑니다',
    steps: [
      { title: '포스트모템', skill: 'nd-incident-intake', detail: '로그·알림·대응 대화로 근본 원인과 단기·중기·장기 액션 아이템을 씁니다', output: '`incident.md`' },
      { title: '문서 리뷰', skill: 'nd-incident-doc-review', detail: '외부 리뷰어가 코드·로그 근거까지 대조합니다. task 분해 전 필수 게이트입니다', output: '`incident-review.md`' },
      { title: 'task 분해', skill: 'nd-incident-to-tasks', detail: '지정한 기간(기본 단기)의 액션 아이템만 task로 쪼갭니다', output: '`TASKS.md` · `tasks/**`' },
      { title: '수정 → 완료', skill: 'nd-incident-loop', detail: '그 기간의 액션 아이템이 하나도 안 남아야 완료됩니다' },
    ],
  },
  {
    key: 'ops',
    title: '🎯 AI-DART 티켓',
    when: '모니터링에서 자동 발급된 운영 티켓을 처리할 때 탑니다',
    steps: [
      { title: '수집 · 판정', skill: 'nd-ops-triage', detail: 'Sentry·GCP 로그를 모아 중복·노이즈·귀속을 가리고 티켓을 발급합니다(스케줄 실행)' },
      { title: '자동 수정', skill: 'nd-ops-autofix', detail: '티켓 하나를 조사 → 수정 → 회귀 테스트 → PR까지 진행합니다. 머지는 항상 사람이 합니다' },
      { title: '사람이 넘겨받기', skill: 'nd-ops-ticket', detail: '자동 수정이 사람에게 넘긴 티켓(needs_human 등)을 내 세션에서 이어 갑니다' },
      { title: '사후 기록', skill: 'nd-ops-record', detail: '머지된 티켓을 하네스 문서로 남기고 지식베이스에 반영합니다' },
    ],
  },
];

export const HARNESS_TOOLS: HarnessTool[] = [
  { skill: 'nd-harness-status', when: '내 피처·이슈가 어디까지 왔고 다음에 뭘 불러야 하는지 모를 때 불러 주세요' },
  { skill: 'nd-harness-doctor', when: '스킬이 안 보이거나 설치·설정이 꼬였을 때 불러 주세요' },
  { skill: 'nd-harness-backfill', when: '하네스 없이 대화로 이미 고친 것을 사후에 문서로 남길 때 씁니다' },
  { skill: 'nd-meeting-notes', when: 'PRD·설계 진행 중 회의로 닫아야 할 결정 안건이 생겼을 때 씁니다' },
  { skill: 'nd-progress-snapshot', when: '구현 중 그룹별 진행률을 한 화면으로 보고 싶을 때 씁니다' },
];
